import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../i18n/LanguageContext';
import { Routine, FetchWithAuth, WorkoutSet } from '../../types';
import { API_URL, getDateKey } from '../../utils';
import { commonDialogStyles } from '../common/dialogStyles';

interface ImportWorkoutDialogProps {
  open: boolean;
  onClose: () => void;
  routines: Routine[];
  fetchWithAuth: FetchWithAuth;
  onImportSuccess: () => void;
  onServiceUnavailable?: () => void;
}

export const ImportWorkoutDialog: React.FC<ImportWorkoutDialogProps> = ({
  open,
  onClose,
  routines,
  fetchWithAuth,
  onImportSuccess,
  onServiceUnavailable
}) => {
  const { t } = useLanguage();
  const [selectedRoutineId, setSelectedRoutineId] = useState<number | ''>('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dayName, setDayName] = useState('');
  const [dayNotes, setDayNotes] = useState('');
  const [workoutsPreview, setWorkoutsPreview] = useState<WorkoutSet[]>([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedRoutineId('');
      setSelectedDate(null);
      setDayName('');
      setDayNotes('');
      setWorkoutsPreview([]);
      setError(null);
    }
  }, [open]);

  const loadWorkoutPreview = useCallback(async () => {
    if (!selectedDate) return;

    setPreviewLoading(true);
    setError(null);
    try {
      const dateKey = getDateKey(selectedDate);
      const response = await fetchWithAuth(`${API_URL}/workouts?date=${dateKey}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch workouts for preview');
      }
      
      const workouts = await response.json();
      setWorkoutsPreview(Array.isArray(workouts) ? workouts : []);
      
      // Auto-generate day name if not set
      if (!dayName && workouts.length > 0) {
        const exerciseNames = workouts.map((w: WorkoutSet) => w.exercise).slice(0, 3);
        const suggestedName = exerciseNames.length <= 3 
          ? exerciseNames.join(', ')
          : `${exerciseNames.slice(0, 2).join(', ')} + ${workouts.length - 2} more`;
        setDayName(suggestedName);
      }
    } catch (error: any) {
      console.error('Error loading workout preview:', error);
      setError(t('importWorkout.previewError'));
      
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedDate, fetchWithAuth, t, dayName, onServiceUnavailable]);

  // Load workout preview when date changes
  useEffect(() => {
    if (selectedDate) {
      loadWorkoutPreview();
    } else {
      setWorkoutsPreview([]);
    }
  }, [selectedDate, loadWorkoutPreview]);

  const handleImport = async () => {
    if (!selectedRoutineId || !selectedDate) return;

    setImporting(true);
    setError(null);
    try {
      const dateKey = getDateKey(selectedDate);
      const response = await fetchWithAuth(`${API_URL}/routines/${selectedRoutineId}/import-from-workout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: dateKey,
          dayName: dayName.trim() || undefined,
          dayNotes: dayNotes.trim() || undefined
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import workout to routine');
      }

      onImportSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error importing workout to routine:', error);
      setError(error.message || t('importWorkout.importError'));
      
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      onClose();
    }
  };

  const canImport = selectedRoutineId && selectedDate && workoutsPreview.length > 0 && !importing;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" sx={commonDialogStyles}>
      <DialogTitle>{t('importWorkout.title')}</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Routine Selection */}
          <FormControl fullWidth>
            <InputLabel>{t('importWorkout.selectRoutine')}</InputLabel>
            <Select
              value={selectedRoutineId}
              label={t('importWorkout.selectRoutine')}
              onChange={(e) => setSelectedRoutineId(e.target.value as number)}
            >
              {routines.filter(r => r.is_active).map((routine) => (
                <MenuItem key={routine.id} value={routine.id}>
                  <Box>
                    <Typography>{routine.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {routine.days.length} {t('routines.days')}, {routine.days.reduce((total, day) => total + day.exercises.length, 0)} {t('routines.exercises')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Selection */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t('importWorkout.selectDate')}
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          {/* Day Name */}
          <TextField
            label={t('importWorkout.dayName')}
            value={dayName}
            onChange={(e) => setDayName(e.target.value)}
            fullWidth
            placeholder={t('importWorkout.dayNamePlaceholder')}
          />

          {/* Day Notes */}
          <TextField
            label={t('importWorkout.dayNotes')}
            value={dayNotes}
            onChange={(e) => setDayNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder={t('importWorkout.dayNotesPlaceholder')}
          />

          {/* Workout Preview */}
          {selectedDate && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('importWorkout.workoutPreview')}
              </Typography>
              
              {previewLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">{t('common.loading')}</Typography>
                </Box>
              ) : workoutsPreview.length > 0 ? (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('importWorkout.exercisesToImport')}:
                  </Typography>
                  {workoutsPreview.map((workout, index) => (
                    <Typography key={index} variant="body2">
                      • {workout.exercise} ({workout.details.length} {t('importWorkout.sets')})
                    </Typography>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {t('importWorkout.importNote')}
                  </Typography>
                </Box>
              ) : (
                <Alert severity="info">
                  {t('importWorkout.noWorkoutsFound')}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!canImport}
          startIcon={importing ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {importing ? t('importWorkout.importing') : t('importWorkout.import')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 